import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { X, UserPlus, Phone, Loader2 } from "lucide-react";
import { Button, Label, PhoneInput, Combobox, cn } from "@tellescope/ui";
import { useDebouncedValue } from "@tellescope/react-hooks";
import type { ComboboxOption } from "@tellescope/ui";
import { useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { formatName } from "@tellescope/utils";
import { useForm } from "@tellescope/forms";

export interface AddAttendeesPanelProps {
  /** Callback when the panel is closed */
  onClose: () => void;
  /** Callback when a team member is added */
  onAddUser: (userId: string, userTwilioNumber?: string) => Promise<boolean>;
  /** Callback when a phone number is added */
  onAddPhone: (phone: string) => Promise<boolean>;
  /** IDs of already added attendees */
  existingAttendees?: string[];
  /** Additional className for the container */
  className?: string;
}

/**
 * AddAttendeesPanel - Panel for adding participants to a conference call.
 *
 * Provides options to add team members from a searchable dropdown or add
 * an external phone number. Uses TanStack Query to search users directly.
 *
 * @example
 * ```tsx
 * <AddAttendeesPanel
 *   onClose={() => setShowAddAttendees(false)}
 *   onAddUser={handleAddAttendeeByUser}
 *   onAddPhone={handleAddAttendeeByPhone}
 *   existingAttendees={conference?.users ?? []}
 * />
 * ```
 */
export function AddAttendeesPanel({
  onClose,
  onAddUser,
  onAddPhone,
  existingAttendees = [],
  className,
}: AddAttendeesPanelProps) {
  const { t } = useTranslation("calling");

  // Team member section state (kept separate - Combobox search tightly coupled to query)
  const [selectedMember, setSelectedMember] = useState<ComboboxOption<string> | undefined>(undefined);
  const [userSearch, setUserSearch] = useState("");
  const [debouncedUserSearch] = useDebouncedValue(userSearch.toLowerCase());
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [userError, setUserError] = useState<string | null>(null);

  // Phone number section via useForm
  const phoneForm = useForm({
    fields: {
      phoneNumber: {
        type: "phone" as const,
        initialValue: "",
        required: true,
      },
    },
    onSubmit: async (values) => {
      const success = await onAddPhone(values.phoneNumber);
      if (success) {
        phoneForm.resetForm();
      } else {
        phoneForm.setFieldError("phoneNumber", t("Failed to add phone number. Please try again."));
      }
    },
  });

  // Query users using TanStack Query (same pattern as enduser search in DialerPanel)
  const usersQuery = useTellescopeInfiniteQuery({
    model: "users",
    method: "getSome",
    request: {
      limit: 100,
      search: debouncedUserSearch ? { query: debouncedUserSearch } : undefined,
    },
    select: (data) => data.pages.flat(),
  });

  // Build options - filter to callable users (only exclude disabled users)
  // Note: Users don't need their own twilioNumber - the org's number can be used as fallback
  const memberOptions = useMemo((): ComboboxOption<string>[] => {
    if (!usersQuery.data) return [];

    return usersQuery.data.reduce<ComboboxOption<string>[]>((acc, user) => {
      // Must not have disabled incoming calls
      if (user.disableIncomingPhoneCalls) return acc;
      // Exclude already added attendees
      if (existingAttendees.includes(user.id)) return acc;

      acc.push({
        value: user.id,
        label: formatName(user),
        metadata: user.twilioNumber ? { twilioNumber: user.twilioNumber } : {},
      });

      return acc;
    }, []);
  }, [usersQuery.data, existingAttendees]);

  // Handle adding a team member
  const handleAddUser = useCallback(async () => {
    if (!selectedMember) return;

    setIsAddingUser(true);
    setUserError(null);

    try {
      const twilioNumber = selectedMember.metadata?.twilioNumber;
      const success = await onAddUser(selectedMember.value, twilioNumber);

      if (success) {
        setSelectedMember(undefined);
      } else {
        setUserError(t("Failed to add team member. Please try again."));
      }
    } catch {
      setUserError(t("Failed to add team member. Please try again."));
    } finally {
      setIsAddingUser(false);
    }
  }, [selectedMember, onAddUser, t]);

  return (
    <div className={cn("border-t bg-muted/30", className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <UserPlus className="h-4 w-4" />
          <span className="font-medium">{t("Add Participants")}</span>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} className="h-6 w-6">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="p-4 space-y-4">
        {/* Team member error message */}
        {userError && <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded">{userError}</div>}

        {/* Team Member Selector */}
        <div className="space-y-2">
          <Label htmlFor="team-member">{t("Invite team member")}</Label>
          <div className="flex gap-2">
            <div className="flex-1">
              <Combobox
                options={memberOptions}
                value={selectedMember}
                onChange={(value) => setSelectedMember(value)}
                searchQuery={userSearch}
                setSearchQuery={setUserSearch}
                customFilter={(_, options) => options}
                placeholder={usersQuery.isLoading ? t("Loading...") : t("Search team members...")}
                searchPlaceholder={t("Type to search...")}
                emptyText={t("No team members available")}
                isLoading={usersQuery.isLoading}
                loadingText={t("Searching...")}
                onLoadMore={() => usersQuery.fetchNextPage()}
                hasMore={usersQuery.hasNextPage}
                isLoadingMore={usersQuery.isFetchingNextPage}
                popoverClassName="z-[3000]"
              />
            </div>
            <Button
              size="sm"
              onClick={handleAddUser}
              disabled={!selectedMember || isAddingUser || usersQuery.isLoading}
              className="px-4"
            >
              {isAddingUser ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Add")}
            </Button>
          </div>
        </div>

        {/* Phone Number Input */}
        <div className="space-y-2">
          <Label htmlFor="phone-number">{t("Add by phone number")}</Label>
          <div className="flex gap-2">
            <div className="flex-1">
              <PhoneInput
                value={phoneForm.values.phoneNumber}
                onChange={(value) => phoneForm.setFieldValue("phoneNumber", value)}
                placeholder={t("Enter phone number")}
                disabled={phoneForm.isSubmitting}
              />
            </div>
            <Button
              size="sm"
              onClick={() => phoneForm.submitForm()}
              disabled={!phoneForm.values.phoneNumber || phoneForm.isSubmitting}
              className="px-4"
            >
              {phoneForm.isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4" />}
            </Button>
          </div>
          {phoneForm.errors.phoneNumber && (
            <div className="text-sm text-destructive">{phoneForm.errors.phoneNumber}</div>
          )}
        </div>

        {/* Done Button */}
        <Button variant="outline" className="w-full" onClick={onClose}>
          {t("Done")}
        </Button>
      </div>
    </div>
  );
}
